import React, { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { PositionSizeRule, TradingStrategyForPortfolio, UserPortfolio } from '@einhaus/project314-types';
import { Button, Callout, Card, ConfirmationDialog, EmptyState, SegmentedControl } from '@/components/UI';
import { trpc } from '@/api/trpc.ts';
import { useTrpcMutation } from '@/api/useTrpcMutation';
import {
    buildStrategySubscriptionFilterOptions,
    buildStrategySubscriptionRows,
    countStrategySubscriptionStatuses,
    describeStrategySubscriptionCounts,
    matchesStrategySubscriptionFilter,
    type StrategySubscriptionFilter,
    type StrategySubscriptionRow,
    type TradingProfileDirectory
} from './strategySubscription';
import StrategySubscriptionTable from './StrategySubscriptionTable';
import LiveStrategyDeactivationDialog from './LiveStrategyDeactivationDialog';
import { useStrategySubscriptionActivation } from './useStrategySubscriptionActivation';

/** Above this many rows the table earns its search box; below it the box is pure clutter. */
const SEARCH_ROW_THRESHOLD = 8;

/** Status filters only help once there are enough rows that scanning them all is work. */
const FILTER_ROW_THRESHOLD = 2;

interface StrategiesSubscribedCardProps {
    portfolio: UserPortfolio | null;
    /** Portfolio-wide sizing, used to show the effective sizing of subscriptions that do not override it. */
    portfolioPositionSizeRules: PositionSizeRule[];
    tradingProfileDirectory: TradingProfileDirectory;
    setShowStrategyModal: React.Dispatch<React.SetStateAction<boolean>>;
    /** Opens the live deployment editor for one subscribed strategy. */
    onConfigureStrategy: (strategyId: number) => void;
}

const StrategiesSubscribedCard: React.FC<StrategiesSubscribedCardProps> = ({
    portfolio,
    portfolioPositionSizeRules,
    tradingProfileDirectory,
    setShowStrategyModal,
    onConfigureStrategy
}) => {
    const queryClient = useQueryClient();

    const [statusFilter, setStatusFilter] = useState<StrategySubscriptionFilter>('all');
    const [strategyPendingRemoval, setStrategyPendingRemoval] = useState<TradingStrategyForPortfolio | null>(null);

    const handleConfigure = useCallback((row: StrategySubscriptionRow) => onConfigureStrategy(row.strategy.id), [onConfigureStrategy]);

    const { activationState, cancelDeactivation, confirmDeactivation, pendingDeactivationRow, requestToggle } =
        useStrategySubscriptionActivation({ onDeploymentIncomplete: handleConfigure });

    const removeStrategyMutation = useTrpcMutation(
        trpc.user.removePortfolioSetting.mutationOptions({
            onSuccess: () => {
                void queryClient.invalidateQueries(trpc.user.fetchPortfolio.pathFilter());
                void queryClient.invalidateQueries(trpc.user.fetchPortfolios.pathFilter());
                setStrategyPendingRemoval(null);
            }
        }),
        {
            successMessage: 'Strategy removed successfully',
            fallbackErrorMessage: 'Failed to remove strategy'
        }
    );

    const rows = useMemo<StrategySubscriptionRow[]>(
        () =>
            portfolio
                ? buildStrategySubscriptionRows({ portfolio, portfolioPositionSizeRules }, tradingProfileDirectory, activationState)
                : [],
        [activationState, portfolio, portfolioPositionSizeRules, tradingProfileDirectory]
    );

    const statusCounts = useMemo(() => countStrategySubscriptionStatuses(rows), [rows]);

    const filterOptions = useMemo(() => buildStrategySubscriptionFilterOptions(rows.length, statusCounts), [rows.length, statusCounts]);

    const summary = rows.length === 0 ? undefined : describeStrategySubscriptionCounts(rows.length, statusCounts);

    const portfolioEnabledRows = rows.filter((row) => row.isActive);
    const blockedEnabledRows = portfolioEnabledRows.filter((row) => row.readiness.status === 'blocked');

    /** The filter control is hidden for very short lists, so it must not keep filtering them either. */
    const activeFilter: StrategySubscriptionFilter = rows.length > FILTER_ROW_THRESHOLD ? statusFilter : 'all';

    const visibleRows = useMemo(
        () => rows.filter((row) => matchesStrategySubscriptionFilter(row.readiness.status, activeFilter)),
        [activeFilter, rows]
    );

    const handleRequestRemoval = useCallback((row: StrategySubscriptionRow) => setStrategyPendingRemoval(row.strategy), []);

    const handleConfirmRemoval = useCallback(() => {
        if (!portfolio || !strategyPendingRemoval) return;

        removeStrategyMutation.mutate({
            portfolioId: portfolio.id,
            settingType: 'strategySubscribed',
            id: strategyPendingRemoval.id
        });
    }, [portfolio, removeStrategyMutation, strategyPendingRemoval]);

    return (
        <>
            <Card
                className="mb-4"
                headerActions={
                    <Button size="sm" variant="primary" onClick={() => setShowStrategyModal(true)}>
                        + Add strategy
                    </Button>
                }
                subtitle={summary}
                title="Strategies Subscribed"
            >
                {rows.length === 0 ? (
                    <EmptyState
                        action={
                            <Button size="sm" variant="secondary" onClick={() => setShowStrategyModal(true)}>
                                Add a strategy
                            </Button>
                        }
                        description="Subscribe a strategy to let this portfolio open positions from it."
                        title="No strategies subscribed."
                    />
                ) : (
                    <div className="space-y-3">
                        {blockedEnabledRows.length > 0 && (
                            <Callout
                                title={`${blockedEnabledRows.length.toLocaleString()} of ${portfolioEnabledRows.length.toLocaleString()} switched-on ${portfolioEnabledRows.length === 1 ? 'strategy is' : 'strategies are'} inactive and will not trade`}
                                variant="warning"
                            >
                                <p>
                                    {blockedEnabledRows.map((row) => row.strategy.name).join(', ')}{' '}
                                    {blockedEnabledRows.length === 1 ? 'is' : 'are'} switched on here but inactive globally, so{' '}
                                    {blockedEnabledRows.length === 1 ? 'it opens' : 'they open'} no new positions. Activate{' '}
                                    {blockedEnabledRows.length === 1 ? 'the strategy' : 'those strategies'}, or turn off the switch here.
                                </p>
                            </Callout>
                        )}

                        {rows.length > FILTER_ROW_THRESHOLD && (
                            <SegmentedControl options={filterOptions} value={statusFilter} onChange={setStatusFilter} />
                        )}

                        {visibleRows.length === 0 ? (
                            <EmptyState size="sm" title="No strategies match this filter." />
                        ) : (
                            <StrategySubscriptionTable
                                identity="strategy"
                                layout="panel"
                                rows={visibleRows}
                                search={rows.length > SEARCH_ROW_THRESHOLD}
                                onConfigure={handleConfigure}
                                onRemove={handleRequestRemoval}
                                onToggleActive={requestToggle}
                            />
                        )}
                    </div>
                )}
            </Card>

            <ConfirmationDialog
                confirmText="Remove strategy"
                isConfirmLoading={removeStrategyMutation.isPending}
                isOpen={strategyPendingRemoval !== null}
                message={
                    <>
                        <p>
                            Remove <span className="font-medium text-white">{strategyPendingRemoval?.name}</span> from this portfolio? It
                            will stop opening new positions here and its live deployment choices are discarded.
                        </p>
                        <p className="mt-2 text-sm text-zinc-400">Existing open positions are not closed by this change.</p>
                    </>
                }
                title="Remove subscribed strategy"
                onCancel={() => setStrategyPendingRemoval(null)}
                onConfirm={handleConfirmRemoval}
            />

            <LiveStrategyDeactivationDialog row={pendingDeactivationRow} onCancel={cancelDeactivation} onConfirm={confirmDeactivation} />
        </>
    );
};

export default StrategiesSubscribedCard;
